// src/components/BoardPack/pages/consolidate_boardpack/Budget_Actual_Chart_Page.jsx
import React, { useEffect, useRef, useState } from "react";
import { useQueue } from "@/context/QueueContext";
import { useNavigate } from "react-router-dom";
import ErrorState from "@/components/common/ErrorStateComponent";
import LoadingComponent from "@/components/common/LoadingComponent";
import { apiCall } from "@/api/apiCall";
import { processParams } from "../../utils/paramsHelper";
import { dynamicKey } from "../../utils/dynamicKey";
import Budget_Actual_Chart_component from "../../components/consolidate_boardpack_components/Budget_Actual_Chart_component";

// ─────────────────────────────────────────────────────────────────────────
// Real API response shape (confirmed via Postman) — data.month / data.ytd,
// har block mein: startLabel/startValueRaw, steps[{label, raw, type}],
// endLabel/endValueRaw. Yahan hum isay chart-ready waterfall series mein
// convert karte hain: [{ label, value, type: "total"|"increase"|"decrease" }]
// ─────────────────────────────────────────────────────────────────────────
const normalizeBridgeBlock = (block) => {
  if (!block) return [];

  return [
    { label: block.startLabel, value: block.startValueRaw, type: "total" },
    ...(block.steps || []).map((s) => ({
      label: s.label,
      value: s.raw,
      type: s.type, // "increase" | "decrease" (API already sends this)
    })),
    { label: block.endLabel, value: block.endValueRaw, type: "total" },
  ];
};

const Budget_Actual_Chart_Page = ({ year = "", month = "", contract = "", page = null, isRefresh = false }) => {
  const { enqueue } = useQueue();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [decSeries, setDecSeries] = useState([]);
  const [ytdSeries, setYtdSeries] = useState([]);
  const [meta, setMeta] = useState(null);
  const hasFetchedRef = useRef(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // measure page config se aata hai (Postman confirm: measure 7 =
      // Budget vs Actual bridge). Har page apna sahi measure khud pass
      // karega (config-driven), yahan hardcode nahi karte.
      const apiParams = processParams(page?.params || {}, {
        year,
        month,
        contract,
        currency: "Reporting Currency",
        is_cfo: 0,
      });

      // NOTE: correct backend path confirmed via working request —
      //   https://apiv2.mawgif.com/tm1backend/cb/boardpack/consolidate-graph
      // Path mein "/cb" segment zaroori hai. Agar apiCall/axios baseURL
      // pehle se "https://apiv2.mawgif.com/tm1backend" set hai, to
      // page.api config mein "/cb/boardpack/consolidate-graph" hona
      // chahiye (sirf "/boardpack/consolidate-graph" nahi — wo galat
      // route hit karta hai jahan measure sirf 0-4 allow hota hai).
      if (!page?.api) {
        throw new Error("Missing 'api' in page config — this chart's endpoint must be set explicitly.");
      }

      const response = await enqueue(() =>
        apiCall("GET", page.api, apiParams, navigate, false, false, isRefresh)
      );

      if (!response?.success) {
        throw new Error(response?.message || "API returned unsuccessful response");
      }

      // Real shape: response.data.month (Dec bridge) + response.data.ytd (YTD bridge)
      setDecSeries(normalizeBridgeBlock(response.data?.month));
      setYtdSeries(normalizeBridgeBlock(response.data?.ytd));
      setMeta(response.meta);
    } catch (err) {
      console.error("Budget_Actual_Chart fetch error:", err);
      setError(err.message || "Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }

  const decChartTitle = page?.decChartTitle ? dynamicKey({ text: page?.decChartTitle, year, month, contract, meta }) : "";
  const ytdChartTitle = page?.ytdChartTitle ? dynamicKey({ text: page?.ytdChartTitle, year, month, contract, meta }) : "";
  return (
    <Budget_Actual_Chart_component
      title={page?.title}
      subtitle={page?.subtitle}
      showComments={page?.showComments}
      decSeries={decSeries}
      ytdSeries={ytdSeries}
      decChartTitle={decChartTitle}
      ytdChartTitle={ytdChartTitle}
      logoSrc={meta?.logoUrl}
      contract={contract}
      year={year}
      month={month}
      meta={meta}
    />
  );
};

export default Budget_Actual_Chart_Page;